import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, ArrowRight } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Field, inputCls, Success } from "@/components/enrol/EnrolmentForm";
import { SERVICES } from "@/data/content";
import { api, formatApiError } from "@/lib/api";

const empty = { name: "", phone: "", email: "", city: "", message: "" };

export default function PatientEnquiry() {
  const [v, setV] = useState(empty);
  const [service, setService] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setV({ ...v, [k]: e.target.value });

  useEffect(() => {
    const on = (e) => { setService(e.detail); setDone(false); };
    window.addEventListener("img:request-service", on);
    return () => window.removeEventListener("img:request-service", on);
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!service) return toast.error("Please choose the service you need");
    setLoading(true);
    try {
      await api.post("/enrolments/patient", { ...v, email: v.email || null, details: { service } });
      setDone(true);
      setV(empty);
      setService("");
      toast.success("Your request has been sent");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  if (done) return <Success type="patient" onReset={() => setDone(false)} />;

  return (
    <form onSubmit={submit} className="grid sm:grid-cols-2 gap-4" data-testid="patient-enquiry-form">
      <div className="sm:col-span-2">
        <Field id="p-service" label="Service needed">
          <Select value={service} onValueChange={setService}>
            <SelectTrigger id="p-service" className={inputCls} data-testid="patient-service-select"><SelectValue placeholder="Select a service" /></SelectTrigger>
            <SelectContent>
              {SERVICES.map((s) => <SelectItem key={s.id} value={s.title} data-testid={`patient-service-option-${s.id}`}>{s.title}</SelectItem>)}
            </SelectContent>
          </Select>
        </Field>
      </div>
      <Field id="p-name" label="Patient / contact name">
        <Input id="p-name" required minLength={2} value={v.name} onChange={set("name")} className={inputCls} data-testid="patient-name-input" />
      </Field>
      <Field id="p-phone" label="Mobile number">
        <Input id="p-phone" type="tel" required minLength={6} value={v.phone} onChange={set("phone")} className={inputCls} data-testid="patient-phone-input" />
      </Field>
      <Field id="p-email" label="Email (optional)">
        <Input id="p-email" type="email" value={v.email} onChange={set("email")} className={inputCls} data-testid="patient-email-input" />
      </Field>
      <Field id="p-city" label="City">
        <Input id="p-city" required minLength={2} value={v.city} onChange={set("city")} className={inputCls} data-testid="patient-city-input" />
      </Field>
      <div className="sm:col-span-2">
        <Field id="p-msg" label="Briefly describe the need (optional)">
          <Textarea id="p-msg" rows={3} value={v.message} onChange={set("message")} className="rounded-xl bg-white border-[#14083E]/15 focus-visible:ring-[#E5157A]" data-testid="patient-message-input" />
        </Field>
      </div>
      <button type="submit" disabled={loading} data-testid="patient-submit-btn"
        className="sm:col-span-2 group inline-flex items-center justify-center gap-2 rounded-full bg-[#14083E] px-8 py-4 font-semibold text-white hover:bg-[#E5157A] transition-colors disabled:opacity-70">
        {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <>Send request <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" /></>}
      </button>
    </form>
  );
}
